import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Sparkles, FileCheck2 } from "lucide-react";

const stats = [
  { value: 500, suffix: "+", label: "Successful Visa Applications", color: "#003366" },
  { value: 25, suffix: "+", label: "International Destinations", color: "#0099CC" },
];

const features = [
  {
    icon: Sparkles,
    title: "Personalized Travel Assistance",
    desc: "Hand-crafted itineraries with dedicated travel concierges from enquiry to return.",
    color: "#D4AF37",
  },
  {
    icon: FileCheck2,
    title: "End-to-End Documentation Support",
    desc: "From DS-160 to financial proofs — every form, every translation, every signature.",
    color: "#003366",
  },
];

function Counter({ to, suffix }) {
  const ref = useRef();
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const dur = 1800;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(to * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span ref={ref}>
      {val.toLocaleString()}
      {suffix}
    </span>
  );
}

export default function Stats() {
  return (
    <section id="stats" data-testid="stats-section" className="relative py-24 md:py-28 bg-offwhite">
      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-gold-dark font-semibold mb-4">— Why Travel Factor</div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tighter text-navy-deep">
            Trust built on <span className="text-gradient-navy">approved journeys</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.7 }}
              className="relative bg-white rounded-3xl p-7 md:p-9 overflow-hidden group border border-navy-deep/8 soft-shadow hover:premium-shadow transition-all duration-500 hover:-translate-y-1"
            >
              <div
                className="absolute -top-12 -right-12 w-44 h-44 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-700"
                style={{ background: `radial-gradient(circle, ${s.color}, transparent 70%)` }}
              />
              <div
                className="font-heading text-5xl md:text-6xl font-bold tracking-tighter mb-3"
                style={{ color: s.color }}
              >
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="text-xs md:text-sm uppercase tracking-[0.22em] text-slate-600 font-semibold leading-snug">
                {s.label}
              </div>
            </motion.div>
          ))}

          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i + 2) * 0.1, duration: 0.7 }}
                className="relative bg-white rounded-3xl p-7 md:p-9 overflow-hidden group border border-navy-deep/8 soft-shadow hover:premium-shadow transition-all duration-500 hover:-translate-y-1"
              >
                <div
                  className="absolute -top-12 -right-12 w-44 h-44 rounded-full opacity-15 group-hover:opacity-35 transition-opacity duration-700"
                  style={{ background: `radial-gradient(circle, ${f.color}, transparent 70%)` }}
                />
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 border"
                  style={{
                    background: `${f.color}15`,
                    borderColor: `${f.color}33`,
                    boxShadow: `0 6px 18px -8px ${f.color}55`,
                  }}
                >
                  <Icon className="w-5 h-5" style={{ color: f.color }} strokeWidth={2.2} />
                </div>
                <h3 className="font-heading text-lg md:text-xl font-bold tracking-tight text-navy-deep mb-2 leading-tight">
                  {f.title}
                </h3>
                <p className="text-[13px] text-slate-600 leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
